// ClipRadar website — How it works + Premiere Pro sections.
import { useEffect, useLayoutEffect, useRef, useState, type ComponentType, type CSSProperties, type ReactNode } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CRWIcon, type IconName } from '../components/icons';
import { SectionHead, Thumb } from '../components/primitives';
import { STILLS } from '../components/stills';
import { useTyping, useSettled, useIsMobile } from '../components/hooks';
import { APP, MiniTimeline, SCENES, type SceneName } from '../components/scenes';
import logoMark from '../assets/logo-mark.svg';

gsap.registerPlugin(ScrollTrigger);

/* ---------- HOW IT WORKS — 3 clickable steps, showcase below ---------- */
export function HowItWorks() {
  const steps: { n: string; title: string; text: string; scene: SceneName }[] = [
    { n: '01', title: 'Ingest & analyze', text: 'Link folders or whole drives. ClipRadar watches everything — transcripts, scene detection, faces, semantic index — entirely on your machine.', scene: 'ingest' },
    { n: '02', title: 'Search & narrow it down', text: 'Type what you remember. Narrow it to a project, a drive, or everything you’ve ever indexed.', scene: 'scope' },
    { n: '03', title: 'Drop on timeline', text: 'Drag any result straight onto your timeline. In and out points preserved, media relinked from your local drive.', scene: 'drop-timeline' },
  ];
  const [active, setActive] = useState(0);
  const [cycle, setCycle] = useState(0);
  const [shown, setShown] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);
  const ref = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);
  const mobile = useIsMobile();
  const chipsRef = useRef<HTMLDivElement | null>(null);
  const DURATION = 7500;

  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.25 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  const seenHow = useRef(false);
  useEffect(() => { if (inView && !seenHow.current) { seenHow.current = true; setCycle((c) => c + 1); } }, [inView]);
  useEffect(() => {
    if (!inView) return;
    const t = setTimeout(() => { setActive((a) => (a + 1) % 3); setCycle((c) => c + 1); }, DURATION);
    return () => clearTimeout(t);
  }, [active, cycle, inView]);

  // polish layer: scroll entrance once + eased scene hand-offs (scenes untouched)
  const sceneBox = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: 'power3.out' },
        scrollTrigger: { trigger: ref.current, start: 'top 72%', once: true },
      });
      tl.from(mobile ? '.crws-chip' : '.crwhow-step', { opacity: 0, y: mobile ? 10 : 16, duration: mobile ? 0.5 : 0.6, stagger: mobile ? 0.06 : 0.1, clearProps: 'transform' }, 0)
        .from('.crwhow-demo', { opacity: 0, y: mobile ? 26 : 34, duration: mobile ? 0.75 : 0.85 }, 0.15);
    }, ref);
    return () => ctx.revert();
  }, [mobile]);

  // mobile: keep the active step chip centered in its scroll row
  useEffect(() => {
    if (!mobile) return;
    const row = chipsRef.current;
    const chip = row?.children[active] as HTMLElement | undefined;
    if (!row || !chip) return;
    const left = chip.offsetLeft - (row.clientWidth - chip.offsetWidth) / 2;
    row.scrollTo({ left, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }, [active, mobile]);
  // scene switch = crossfade: outgoing stays solid (settled) while the incoming
  // materializes over it (opacity + whisper of blur), then the outgoing drops.
  useLayoutEffect(() => {
    if (active === shown) return;
    setPrev(shown);
    setShown(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
  useLayoutEffect(() => {
    if (prev == null) return;
    const inc = sceneBox.current;
    if (!inc || window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setPrev(null); return; }
    let killed = false;
    gsap.fromTo(inc, { opacity: 0, filter: 'blur(6px)' }, { opacity: 1, filter: 'blur(0px)', duration: 0.5, ease: 'power2.out', overwrite: 'auto', onComplete: () => { if (!killed) setPrev(null); } });
    return () => { killed = true; };
  }, [prev, shown]);

  const sceneAt = (i: number) => { const S: ComponentType = SCENES[steps[i].scene]; return <S />; };

  // mobile: step chips directly above the scene — the switch happens in view,
  // the active step's text rides below as a crossfading caption.
  if (mobile) {
    return (
      <section id="how" ref={ref} style={{ padding: '56px 0', position: 'relative' }}>
        <div style={{ padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 22 }}>
          <span className="crw-eyebrow"><i></i>How it works</span>
          <h2 className="crw-h-grad" style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 'clamp(28px, 8vw, 34px)', fontWeight: 600, letterSpacing: 'var(--tracking-display)', lineHeight: 1.1 }}>Three steps.<br />Zero scrubbing.</h2>
        </div>
        <div className="crws-chips" ref={chipsRef} role="tablist" aria-label="How it works">
          {steps.map((s, i) => {
            const on = i === active;
            return (
              <button key={i} className={`crws-chip${on ? ' crws-chip-on' : ''}`} role="tab" aria-selected={on} onClick={() => { setActive(i); setCycle((c) => c + 1); }}>
                {on && inView ? <span key={cycle} className="crw-progress" style={{ animationDuration: `${DURATION}ms` }}></span> : null}
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.08em', color: on ? 'var(--fg-2)' : 'var(--fg-4)' }}>{s.n}</span>
                {s.title}
              </button>
            );
          })}
        </div>
        <div style={{ padding: '0 20px' }}>
          <div className="crwhow-demo" style={{ height: 'min(440px, 118vw)', position: 'relative' }}>
            {prev != null ? (
              <div key={`prev-${prev}`} data-settled="true" style={{ position: 'absolute', inset: 0 }}>{sceneAt(prev)}</div>
            ) : null}
            <div key={`${shown}-${cycle}`} ref={sceneBox} style={{ position: 'absolute', inset: 0 }}>{sceneAt(shown)}</div>
          </div>
        </div>
        <p key={active} className="crw-fade crws-mobiletext">{steps[active].text}</p>
      </section>
    );
  }

  return (
    <section id="how" ref={ref} style={{ padding: '90px 24px', position: 'relative' }}>
      <div aria-hidden="true" style={{ position: 'absolute', left: '60%', top: '20%', width: 700, height: 700, background: 'radial-gradient(circle, rgba(var(--light-rgb),0.06), transparent 65%)', pointerEvents: 'none' }}></div>
      <div style={{ maxWidth: 1120, margin: '0 auto', position: 'relative' }}>
        <div className="crw-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 40, alignItems: 'start', marginBottom: 40 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <span className="crw-eyebrow"><i></i>How it works</span>
            <h2 className="crw-h-grad" style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 'clamp(28px, 3.6vw, 40px)', fontWeight: 700, letterSpacing: 'var(--tracking-display)', lineHeight: 1.1 }}>Three steps.<br />Zero scrubbing.</h2>
          </div>
          <div className="crw-how-steps" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {steps.map((s, i) => {
              const on = i === active;
              return (
                <button key={i} className="crwhow-step crw-steptab" onClick={() => { setActive(i); setCycle((c) => c + 1); }} style={{ position: 'relative', overflow: 'hidden', padding: '16px 16px 14px', borderRadius: 12, border: '1px solid', borderColor: on ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)', background: on ? 'linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.02))' : 'rgba(255,255,255,0.015)', boxShadow: on ? 'inset 0 1px 0 rgba(255,255,255,0.1)' : 'none' }}>
                  {on && inView ? <span key={cycle} className="crw-progress" style={{ animationDuration: `${DURATION}ms` }}></span> : null}
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: on ? 'var(--fg-2)' : 'var(--fg-4)', letterSpacing: '0.08em' }}>STEP {s.n}</span>
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: on ? 'var(--fg-1)' : 'var(--fg-3)', marginTop: 5 }}>{s.title}</div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="crw-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 40, alignItems: 'center' }}>
          <p key={active} className="crw-fade" style={{ margin: 0, fontSize: 16, lineHeight: 1.7, color: 'var(--fg-2)' }}>{steps[active].text}</p>
          <div className="crwhow-demo" style={{ height: 500, position: 'relative' }}>
            {prev != null ? (
              <div key={`prev-${prev}`} data-settled="true" style={{ position: 'absolute', inset: 0 }}>{sceneAt(prev)}</div>
            ) : null}
            <div key={`${shown}-${cycle}`} ref={sceneBox} style={{ position: 'absolute', inset: 0 }}>{sceneAt(shown)}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- INSIDE PREMIERE — NLE mock scene ---------- */
function PrTile({ size = 22, fs = 10 }: { size?: number; fs?: number }) {
  return (
    <span style={{ width: size, height: size, flex: 'none', borderRadius: size * 0.22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#1A1034', border: '1px solid rgba(153,153,255,0.4)', color: '#9999FF', fontWeight: 700, fontSize: fs, fontFamily: 'var(--font-body)', letterSpacing: '-0.02em' }}>Pr</span>
  );
}

type PremiereVariant = 'dock' | 'search' | 'drag';

// Miniature of the REAL ClipRadar panel as it docks inside Premiere.
function MiniPanel({ variant, hl }: { variant: PremiereVariant; hl: React.CSSProperties }) {
  const { typed, done } = useTyping(variant === 'search' ? 'crowds at the inauguration' : '', { delay: 400 });
  const mono = 'var(--font-mono)';
  // full-width mobile mock: the desktop dock variant's "recents only" story
  // leaves a chasm of empty panel — show the last search's results instead
  const mobile = useIsMobile();
  return (
    <div className={variant === 'dock' ? 'crw-pop' : ''} style={{ borderRadius: 6, background: APP.bg, border: '1px solid var(--line-2)', display: 'flex', flexDirection: 'column', overflow: 'hidden', transition: 'border-color 300ms var(--ease-out), box-shadow 300ms var(--ease-out)', ...hl }}>
      {/* panel tab strip */}
      <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: '#1D1D21', borderBottom: '1px solid rgba(0,0,0,0.5)' }}>
        <img src={logoMark} width="10" height="10" alt="" />
        <span style={{ fontSize: 8.5, fontWeight: 600, color: APP.text }}>ClipRadar</span>
        <span style={{ flex: 1 }}></span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 7.5, color: APP.green }}><i style={{ width: 4, height: 4, borderRadius: '50%', background: APP.green, display: 'inline-block' }}></i> Online</span>
      </div>
      <div style={{ flex: 1, minHeight: 0, padding: 7, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {/* mini search box */}
        <div style={{ borderRadius: 7, background: APP.panel, border: '1px solid', borderColor: variant === 'search' && done ? APP.focus : APP.chipBorder, boxShadow: variant === 'search' && done ? '0 0 0 2px rgba(96,158,250,0.12)' : 'none', transition: 'border-color 300ms var(--ease-out), box-shadow 300ms var(--ease-out)', padding: '5px 6px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 4, fontSize: 7, color: APP.text, background: APP.chip, border: `1px dashed ${APP.chipBorder}` }}><CRWIcon d="at" size={7} /> All analyzed media</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: 8.5, color: typed ? APP.text : APP.dim, whiteSpace: 'nowrap', overflow: 'hidden' }}>{typed || 'Search your footage…'}{variant === 'search' && !done ? <span className="crw-caret" style={{ width: 4, height: 8 }}></span> : null}</span>
            <span style={{ width: 16, height: 14, flex: 'none', borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: APP.blue, color: '#F2F9FF' }}><CRWIcon d="arrowRight" size={8} sw={2.4} /></span>
          </span>
        </div>
        {/* placement icons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ display: 'inline-flex', gap: 1, padding: 1, borderRadius: 5, border: `1px solid ${APP.line}` }}>
            {(['wand', 'downToLine', 'toLine'] as const).map((ic, i) => (
              <span key={ic} style={{ width: 16, height: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4, color: i === 0 ? APP.text : APP.dim, background: i === 0 ? APP.active : 'transparent' }}><CRWIcon d={ic} size={8} /></span>
            ))}
          </span>
          <span style={{ flex: 1 }}></span>
          <span style={{ fontSize: 7.5, fontWeight: 600, color: variant === 'drag' ? '#F2F9FF' : APP.dim, padding: '2px 7px', borderRadius: 4, background: variant === 'drag' ? APP.blue : 'transparent', border: variant === 'drag' ? '1px solid transparent' : `1px solid ${APP.line}`, display: 'inline-flex', alignItems: 'center', gap: 2 }}><CRWIcon d="plus" size={7} sw={2.6} /> Place</span>
        </div>
        {/* results — the freshly-docked panel hasn't searched yet, so it only
            shows recents; results appear in the search/drag variants (and in
            every variant on mobile, where the empty panel would gape) */}
        {variant === 'dock' ? (
          <div style={{ flex: 'none', display: 'flex', gap: 3, alignItems: 'center' }}>
            {['crowds at the inauguration', 'the rocket on the pad'].map((h) => (
              <span key={h} style={{ fontSize: 6.5, color: APP.dim, padding: '1px 6px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.06)', whiteSpace: 'nowrap' }}>{h}</span>
            ))}
            <span style={{ flex: 1 }}></span>
            <span style={{ fontSize: 6.5, color: APP.dim }}>Clear</span>
          </div>
        ) : null}
        {variant !== 'dock' || mobile ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5, flex: 1, alignContent: 'start' }}>
            {[
              { img: STILLS.paradecrowd, pos: '50% 50%', zoom: 1, tc: '00:00:19 - 00:00:24' },
              { img: STILLS.capitolcrowd, pos: '50% 55%', zoom: 1, tc: '00:00:48 - 00:00:52' },
              { img: STILLS.paradestands, pos: '50% 50%', zoom: 1, tc: '00:00:10 - 00:00:13' },
              { img: STILLS.motorcade, pos: '50% 50%', zoom: 1, tc: '00:00:25 - 00:00:30' },
            ].map((c, i) => (
              // thumb + timecode stagger in TOGETHER — a result is one unit
              <div key={i} className={variant === 'search' ? 'crw-pop' : undefined} style={{ position: 'relative', animationDelay: variant === 'search' ? `${1100 + i * 120}ms` : undefined }}>
                <Thumb img={c.img} pos={c.pos} zoom={c.zoom} ar="16 / 9" delay={0} />
                <span style={{ display: 'block', fontFamily: mono, fontSize: 6.5, color: APP.blueText, marginTop: 2 }}>{c.tc}</span>
                {variant === 'drag' && i === 0 ? <span className="crw-dragghost" style={{ position: 'absolute', inset: '-2px', borderRadius: 6, border: '1.5px solid rgba(96,158,250,0.8)', boxShadow: '0 0 10px rgba(80,130,240,0.3)' }}></span> : null}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ flex: 1 }}></div>
        )}
      </div>
    </div>
  );
}

// One NLE monitor — panel tab, real frame, scrub bar, transport + timecodes.
function NleMonitor({ tab, tc, dur, img, pos, at }: { tab: string; tc: string; dur: string; img: string; pos?: string; at: string }) {
  const mono = 'var(--font-mono)';
  return (
    <div className="crw-nle-monitor" style={{ borderRadius: 6, background: '#101013', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ flex: 'none', display: 'flex', alignItems: 'stretch', background: '#1D1D21', borderBottom: '1px solid rgba(0,0,0,0.5)' }}>
        <span style={{ padding: '3px 9px 4px', fontSize: 7.5, color: '#C8C8CE', background: '#101013', borderTop: '1.5px solid rgba(96,158,250,0.8)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tab}</span>
        <span style={{ flex: 1 }}></span>
        <span style={{ alignSelf: 'center', color: '#6e6e78', fontSize: 10, paddingRight: 6 }}>≡</span>
      </div>
      <div style={{ flex: 1, margin: '6px 7px 0', borderRadius: 4, background: '#000', position: 'relative', minHeight: 0, overflow: 'hidden' }}>
        <img src={img} alt="" loading="lazy" decoding="async" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: pos ?? '50% 50%', display: 'block' }} />
        <span style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.2), transparent 28%, transparent 70%, rgba(0,0,0,0.35))', borderRadius: 4 }}></span>
        <span style={{ position: 'absolute', right: 5, top: 4, fontFamily: mono, fontSize: 6.5, color: 'rgba(255,255,255,0.6)' }}>1/2</span>
      </div>
      {/* scrub bar */}
      <div style={{ flex: 'none', margin: '5px 9px 0', height: 2, borderRadius: 2, background: 'rgba(255,255,255,0.1)', position: 'relative' }}>
        <i style={{ position: 'absolute', left: at, top: -2.5, width: 7, height: 7, borderRadius: '50%', background: '#609EFA' }}></i>
      </div>
      <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 6, padding: '3px 9px 6px' }}>
        <span style={{ fontFamily: mono, fontSize: 7.5, color: '#609EFA' }}>{tc}</span>
        <span style={{ flex: 1, textAlign: 'center', fontFamily: mono, fontSize: 8, color: '#9b9ba3', letterSpacing: 3 }}>◀◀ ◀ ▶ ▶▶</span>
        <span style={{ fontFamily: mono, fontSize: 7.5, color: '#9b9ba3' }}>{dur}</span>
      </div>
    </div>
  );
}

function PremiereMock({ variant }: { variant: PremiereVariant }) {
  const settled = useSettled();
  const hl = (cond: boolean) => (cond ? { borderColor: 'rgba(var(--light-mid-rgb),0.55)', boxShadow: '0 0 18px rgba(var(--light-rgb),0.14)' } : {});
  const workspaces = ['Assembly', 'Editing', 'Color', 'Effects', 'Audio'];
  return (
    <div data-settled={settled ? 'true' : undefined} style={{ height: '100%', borderRadius: 16, padding: 1, background: 'linear-gradient(160deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04) 35%, rgba(var(--light-soft-rgb),0.22) 75%, rgba(255,255,255,0.05))', boxShadow: '0 40px 100px rgba(0,0,0,0.65), 0 0 110px rgba(var(--light-rgb),0.07)' }}>
      <div style={{ height: '100%', borderRadius: 15, overflow: 'hidden', background: '#101013', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', borderBottom: '1px solid rgba(0,0,0,0.5)', background: '#1D1D21' }}>
          <span style={{ display: 'flex', gap: 5 }}>
            {['#FF5F57', '#FEBC2E', '#28C840'].map((c) => <i key={c} style={{ width: 9, height: 9, borderRadius: '50%', background: c, opacity: 0.75 }}></i>)}
          </span>
          <PrTile size={16} fs={8} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '0.05em', color: '#9b9ba3', marginLeft: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>ADOBE PREMIERE PRO — APOLLO_DOCU.PRPROJ</span>
        </div>
        {/* Import / Edit / Export + workspaces strip */}
        <div className="crw-nle-workspaces" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 12, padding: '4px 14px', background: '#19191C', borderBottom: '1px solid rgba(0,0,0,0.5)' }}>
          {['Import', 'Edit', 'Export'].map((m) => (
            <span key={m} style={{ fontSize: 8.5, fontWeight: m === 'Edit' ? 600 : 400, color: m === 'Edit' ? '#DEDEDE' : '#6e6e78', borderBottom: m === 'Edit' ? '1.5px solid #609EFA' : '1.5px solid transparent', paddingBottom: 1 }}>{m}</span>
          ))}
          <span style={{ flex: 1 }}></span>
          {workspaces.map((w) => (
            <span key={w} style={{ fontSize: 8, color: w === 'Editing' ? '#DEDEDE' : '#55555e', fontWeight: w === 'Editing' ? 600 : 400 }}>{w}</span>
          ))}
          <span style={{ flex: 1 }}></span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 7.5, color: '#55555e' }}>FULL · 59.94</span>
        </div>
        <div className="crw-nle-grid" style={{ flex: 1, display: 'grid', gridTemplateColumns: '1.1fr 1.1fr 0.9fr', gap: 6, padding: 6, minHeight: 0 }}>
          <NleMonitor tab="Source: jfk-inauguration-parade.mp4" tc="00:00:19:04" dur="00:00:35" at="54%" img={STILLS.paradecrowd} pos="50% 50%" />
          <NleMonitor tab="Program: Sequence 04" tc="00:08:21:07" dur="04:32:18" at="58%" img={STILLS.climb} pos="50% 40%" />
          <MiniPanel variant={variant} hl={hl(variant === 'dock')} />
        </div>
        <div style={{ flex: 'none', padding: 6, paddingTop: 0 }}>
          <MiniTimeline dropDelay={variant === 'drag' ? 900 : null} dropLabel="PARADE · 0:19–0:24" />
        </div>
      </div>
    </div>
  );
}

export function PremiereSection() {
  const tabs: { id: PremiereVariant; title: string; text: string; icon: IconName }[] = [
    { id: 'dock', title: 'A real docked panel', text: 'Not a browser tab — a native panel that docks anywhere in your workspace.', icon: 'monitor' },
    { id: 'search', title: 'Search without leaving', text: 'Query your entire library mid-edit. Results preview right in the panel.', icon: 'search' },
    { id: 'drag', title: 'Drag straight to timeline', text: 'Results are real media. Drag them onto the sequence like any clip.', icon: 'arrowRight' },
  ];
  const [active, setActive] = useState(0);
  const [cycle, setCycle] = useState(0);
  const ref = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);
  const [shown, setShown] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);
  const premBox = useRef<HTMLDivElement | null>(null);
  const mobile = useIsMobile();
  const chipsRef = useRef<HTMLDivElement | null>(null);
  const DURATION = 6500;
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.25 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  const seenPrem = useRef(false);
  useEffect(() => { if (inView && !seenPrem.current) { seenPrem.current = true; setCycle((c) => c + 1); } }, [inView]);
  useEffect(() => {
    if (!inView) return;
    const t = setTimeout(() => { setActive((a) => (a + 1) % 3); setCycle((c) => c + 1); }, DURATION);
    return () => clearTimeout(t);
  }, [active, cycle, inView]);
  // crossfade between the dock/search/drag variants — outgoing stays solid while
  // the incoming materializes over it (was a hard remount pop, then a flashy fade)
  useLayoutEffect(() => {
    if (active === shown) return;
    setPrev(shown);
    setShown(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
  useLayoutEffect(() => {
    if (prev == null) return;
    const inc = premBox.current;
    if (!inc || window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setPrev(null); return; }
    let killed = false;
    gsap.fromTo(inc, { opacity: 0, filter: 'blur(6px)' }, { opacity: 1, filter: 'blur(0px)', duration: 0.5, ease: 'power2.out', overwrite: 'auto', onComplete: () => { if (!killed) setPrev(null); } });
    return () => { killed = true; };
  }, [prev, shown]);

  // mobile: keep the active capability chip centered in its scroll row
  useEffect(() => {
    if (!mobile) return;
    const row = chipsRef.current;
    const chip = row?.children[active] as HTMLElement | undefined;
    if (!row || !chip) return;
    const left = chip.offsetLeft - (row.clientWidth - chip.offsetWidth) / 2;
    row.scrollTo({ left, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }, [active, mobile]);

  // mobile: chips above the docked-panel mock, the active move's text below it
  if (mobile) {
    return (
      <section id="premiere" ref={ref} style={{ padding: '56px 0 40px' }}>
        <div style={{ padding: '0 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
            <span className="crw-glass" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '8px 16px', borderRadius: 999 }}>
              <PrTile size={24} fs={11} />
              <span style={{ fontSize: 12.5, color: 'var(--fg-2)' }}>Native Adobe Premiere Pro panel</span>
            </span>
          </div>
          <SectionHead center title="Your library lives in your NLE." sub="ClipRadar ships as a Premiere Pro panel. Search, preview and place — without ever leaving the edit." />
        </div>
        <div className="crws-chips" ref={chipsRef} role="tablist" aria-label="Premiere panel capabilities">
          {tabs.map((t, i) => {
            const on = i === active;
            return (
              <button key={t.id} className={`crws-chip${on ? ' crws-chip-on' : ''}`} role="tab" aria-selected={on} onClick={() => { setActive(i); setCycle((c) => c + 1); }}>
                {on && inView ? <span key={cycle} className="crw-progress" style={{ animationDuration: `${DURATION}ms` }}></span> : null}
                <span className="crws-chip-icon"><CRWIcon d={t.icon} size={13} /></span>
                {t.title}
              </button>
            );
          })}
        </div>
        <div style={{ padding: '0 20px' }}>
          <div style={{ height: 'min(460px, 124vw)', position: 'relative' }}>
            <div aria-hidden="true" style={{ position: 'absolute', inset: '-70px -90px', background: 'radial-gradient(ellipse 55% 55% at 50% 40%, rgba(var(--light-rgb),0.12), transparent 70%)', pointerEvents: 'none' }}></div>
            {prev != null ? (
              <div key={`prev-${prev}`} data-settled="true" style={{ position: 'absolute', inset: 0 }}><PremiereMock variant={tabs[prev].id} /></div>
            ) : null}
            <div key={`${shown}-${cycle}`} ref={premBox} style={{ position: 'absolute', inset: 0 }}><PremiereMock variant={tabs[shown].id} /></div>
          </div>
        </div>
        <p key={active} className="crw-fade crws-mobiletext">{tabs[active].text}</p>
      </section>
    );
  }

  return (
    <section id="premiere" ref={ref} style={{ padding: '90px 24px 50px' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
          <span className="crw-glass" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '8px 16px', borderRadius: 999 }}>
            <PrTile size={24} fs={11} />
            <span style={{ fontSize: 12.5, color: 'var(--fg-2)' }}>Native Adobe Premiere Pro panel</span>
          </span>
        </div>
        <SectionHead center title="Your library lives in your NLE." sub="ClipRadar ships as a Premiere Pro panel. Search, preview and place — without ever leaving the edit." />
        <div style={{ height: 560, maxWidth: 1040, margin: '0 auto', position: 'relative' }}>
          <div aria-hidden="true" style={{ position: 'absolute', inset: '-90px -120px', background: 'radial-gradient(ellipse 55% 55% at 50% 40%, rgba(var(--light-rgb),0.12), transparent 70%)', pointerEvents: 'none' }}></div>
          {prev != null ? (
            <div key={`prev-${prev}`} data-settled="true" style={{ position: 'absolute', inset: 0 }}><PremiereMock variant={tabs[prev].id} /></div>
          ) : null}
          <div key={`${shown}-${cycle}`} ref={premBox} style={{ position: 'absolute', inset: 0 }}><PremiereMock variant={tabs[shown].id} /></div>
        </div>
        <div className="crw-how-steps" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, maxWidth: 1040, margin: '20px auto 0' }}>
          {tabs.map((t, i) => {
            const on = i === active;
            return (
              <button key={t.id} className="crw-steptab" onClick={() => { setActive(i); setCycle((c) => c + 1); }} style={{ position: 'relative', overflow: 'hidden', padding: '14px 16px', borderRadius: 12, border: '1px solid', borderColor: on ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)', background: on ? 'linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.02))' : 'rgba(255,255,255,0.015)', boxShadow: on ? 'inset 0 1px 0 rgba(255,255,255,0.1)' : 'none' }}>
                {on && inView ? <span key={cycle} className="crw-progress" style={{ animationDuration: `${DURATION}ms` }}></span> : null}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                  <span style={{ color: on ? 'var(--fg-2)' : 'var(--fg-4)', display: 'inline-flex' }}><CRWIcon d={t.icon} size={14} /></span>
                  <b style={{ fontSize: 14, fontWeight: 600, color: on ? 'var(--fg-1)' : 'var(--fg-3)' }}>{t.title}</b>
                </div>
                <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: 'var(--fg-3)' }}>{t.text}</p>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- MORE PREMIERE CAPABILITIES — card grid with animated mini-demos ---------- */
type MicroKind = 'smart' | 'batch' | 'match' | 'source' | 'videoonly' | 'transcript';

const microMono = 'var(--font-mono)';

// Playhead line spanning the stacked tracks of a mini timeline.
function MicroPlayhead({ left }: { left: string }) {
  return (
    <span aria-hidden="true" style={{ position: 'absolute', left, top: -2, bottom: 0, width: 1.5, background: 'rgba(255,255,255,0.55)', boxShadow: '0 0 6px rgba(255,255,255,0.18)', zIndex: 2, pointerEvents: 'none' }}>
      <i style={{ position: 'absolute', top: -1, left: -3.5, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: '5px solid rgba(255,255,255,0.55)' }}></i>
    </span>
  );
}

function MicroTrack({ label, h = 15, children }: { label: string; h?: number; children?: ReactNode }) {
  return (
    <div style={{ position: 'relative', height: h, borderRadius: 4, background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.05)' }}>
      <span style={{ position: 'absolute', left: 5, top: '50%', transform: 'translateY(-50%)', fontFamily: microMono, fontSize: 7, color: 'var(--fg-4)', zIndex: 1 }}>{label}</span>
      {children}
    </div>
  );
}

function MicroClip({ l, w, hue = 250, lit, className, style, children }: { l: string; w: string; hue?: number; lit?: boolean; className?: string; style?: CSSProperties; children?: ReactNode }) {
  return (
    <span className={className} style={{ position: 'absolute', left: l, width: w, top: 1.5, bottom: 1.5, borderRadius: 3, background: lit ? 'rgba(var(--light-mid-rgb),0.13)' : `oklch(0.32 0.05 ${hue})`, border: `1px solid ${lit ? 'rgba(var(--light-mid-rgb),0.45)' : 'rgba(255,255,255,0.1)'}`, display: 'inline-flex', alignItems: 'center', overflow: 'hidden', whiteSpace: 'nowrap', ...style }}>{children}</span>
  );
}

// Mono caption under a mini-demo; `sync` fades it in step with the 4.2s demo
// loop. Captions live ONLY on the two hero cards now, so they sit one step
// brighter than the small-card copy (fg-2, not fg-3).
function MicroCap({ children, sync }: { children: ReactNode; sync?: boolean }) {
  return <div className={sync ? 'crw-syncnote' : undefined} style={{ fontFamily: microMono, fontSize: 7.5, letterSpacing: '0.07em', color: 'var(--fg-2)', marginTop: 7, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</div>;
}

function MicroClips({ kind }: { kind: MicroKind }) {
  if (kind === 'smart') return (
    // The selected segment lands AT the playhead — V1 is busy there, so it takes V2.
    <div>
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 4 }}>
        <MicroPlayhead left="44%" />
        <MicroTrack label="V2">
          <MicroClip className="crw-smartdrop" l="44%" w="34%" lit>
            <span style={{ fontFamily: microMono, fontSize: 6.5, color: 'var(--signal-300)', padding: '0 5px' }}>MOONWALK · 4s</span>
          </MicroClip>
        </MicroTrack>
        <MicroTrack label="V1">
          <MicroClip l="16%" w="42%" hue={250} />
          <MicroClip l="62%" w="28%" hue={270} />
        </MicroTrack>
      </div>
      <MicroCap sync>✓ V1 BUSY AT PLAYHEAD → PLACED ON V2 · NOTHING OVERWRITTEN</MicroCap>
    </div>
  );
  if (kind === 'batch') return (
    <div>
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', paddingTop: 4 }}>
        <MicroPlayhead left="9%" />
        <MicroTrack label="V1" h={18}>
          {[0, 1, 2].map((i) => (
            <MicroClip key={i} className="crw-batchdrop" l={`${9 + i * 28}%`} w="27%" lit style={{ animationDelay: `calc(var(--cd, 0s) + ${i * 0.45}s)` }}>
              <span style={{ fontFamily: microMono, fontSize: 6.5, color: 'var(--signal-300)', padding: '0 5px' }}>0{i + 1}</span>
            </MicroClip>
          ))}
        </MicroTrack>
      </div>
    </div>
  );
  if (kind === 'match') return (
    <div>
      <div style={{ display: 'flex', gap: 7, alignItems: 'stretch' }}>
        <div style={{ position: 'relative', flex: 1.15, borderRadius: 5, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', minHeight: 44, background: 'linear-gradient(135deg, oklch(0.34 0.06 250), oklch(0.16 0.04 290))' }}>
          <span className="crw-scanframe"></span>
          <span style={{ position: 'absolute', left: 5, top: 4, fontFamily: microMono, fontSize: 6.5, color: 'rgba(255,255,255,0.65)', letterSpacing: '0.05em' }}>PROGRAM · 00:42:17</span>
          <span style={{ position: 'absolute', right: 4, bottom: 3, color: 'rgba(255,255,255,0.6)', display: 'inline-flex' }}><CRWIcon d="camera" size={9} /></span>
        </div>
        <span style={{ alignSelf: 'center', color: 'var(--fg-3)', display: 'inline-flex', flex: 'none' }}><CRWIcon d="arrowRight" size={11} /></span>
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, alignContent: 'center' }}>
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className="crw-loopin" style={{ animationDelay: `calc(var(--cd, 0s) + ${1 + i * 0.25}s)`, height: 19, borderRadius: 4, border: '1px solid rgba(255,255,255,0.12)', background: `linear-gradient(${120 + i * 24}deg, oklch(0.32 0.05 ${252 - i * 9}), oklch(0.15 0.03 285))` }}></span>
          ))}
        </div>
      </div>
    </div>
  );
  if (kind === 'source') return (
    <div>
      <div style={{ position: 'relative', height: 44, borderRadius: 5, background: 'linear-gradient(135deg, oklch(0.28 0.05 240), oklch(0.14 0.03 270))', border: '1px solid rgba(255,255,255,0.08)' }}>
        <span style={{ position: 'absolute', left: 5, top: 4, fontFamily: microMono, fontSize: 6.5, color: 'rgba(255,255,255,0.65)', letterSpacing: '0.05em' }}>SOURCE MONITOR</span>
        <span className="crw-outdrag" style={{ position: 'absolute', left: '24%', top: 0, bottom: 0, background: 'rgba(var(--light-mid-rgb),0.13)', borderLeft: '2px solid var(--signal-400)', borderRight: '2px solid var(--signal-400)' }}>
          <i style={{ position: 'absolute', left: 2, bottom: 2, fontFamily: microMono, fontSize: 6.5, fontStyle: 'normal', color: 'var(--signal-300)' }}>IN</i>
          <i style={{ position: 'absolute', right: 2, bottom: 2, fontFamily: microMono, fontSize: 6.5, fontStyle: 'normal', color: 'var(--signal-300)' }}>OUT</i>
        </span>
      </div>
    </div>
  );
  if (kind === 'videoonly') return (
    <div>
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 4 }}>
        <MicroTrack label="V1" h={16}>
          <MicroClip className="crw-smartdrop" l="30%" w="42%" lit>
            <span style={{ fontFamily: microMono, fontSize: 6.5, color: 'var(--signal-300)', padding: '0 5px' }}>PICTURE</span>
          </MicroClip>
        </MicroTrack>
        <MicroTrack label="A1" h={14}>
          <span style={{ position: 'absolute', left: 22, right: 20, top: 0, bottom: 0, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {Array.from({ length: 26 }, (_, i) => (
              <i key={i} style={{ flex: 1, height: `${28 + ((i * 37) % 50)}%`, borderRadius: 1, background: 'rgba(60,230,160,0.35)' }}></i>
            ))}
          </span>
          <span style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', color: 'var(--fg-3)', display: 'inline-flex' }}><CRWIcon d="lock" size={8} /></span>
        </MicroTrack>
      </div>
    </div>
  );
  if (kind === 'transcript') {
    // real rows from apollo11-press-conference-1969.mp4 (speech.sqlite segs
    // 1, 3, 4, 5 — verbatim, truncated with ellipses; real segment starts)
    const rows: [string, string, boolean][] = [
      ['00:00:03', 'would each of you give us your estimate of what is the meaning…', false],
      ['00:00:20', 'I believe that what this country set out to do was something…', true],
      ['00:00:37', 'sooner or later, whether we set a specific goal or not', true],
      ['00:00:43', 'I believe that from the early space flights, we demonstrated…', false],
    ];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {rows.map(([t, txt, sel], i) => (
          <div key={i} className={sel ? 'crw-rowsel' : undefined} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 6px', borderRadius: 5, ...(sel && i === 2 ? { animationDelay: 'calc(var(--cd, 0s) + 0.35s)' } : null) }}>
            <span style={{ fontFamily: microMono, fontSize: 7.5, color: sel ? '#9EC2FF' : 'var(--fg-4)', flex: 'none' }}>{t}</span>
            <span style={{ fontSize: 8.5, color: sel ? 'var(--fg-2)' : 'var(--fg-4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{txt}</span>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 6 }}>
          <span style={{ fontFamily: microMono, fontSize: 7, color: 'var(--fg-4)', letterSpacing: '0.08em', flex: 'none' }}>NEW SEQUENCE</span>
          <div style={{ position: 'relative', flex: 1, height: 14, borderRadius: 4, background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <span className="crw-loopin" style={{ animationDelay: 'calc(var(--cd, 0s) + 1.1s)', position: 'absolute', left: '2%', width: '30%', top: 1.5, bottom: 1.5, borderRadius: 3, background: 'rgba(var(--light-mid-rgb),0.13)', border: '1px solid rgba(var(--light-mid-rgb),0.45)' }}></span>
            <span className="crw-loopin" style={{ animationDelay: 'calc(var(--cd, 0s) + 1.45s)', position: 'absolute', left: '34%', width: '26%', top: 1.5, bottom: 1.5, borderRadius: 3, background: 'rgba(var(--light-mid-rgb),0.13)', border: '1px solid rgba(var(--light-mid-rgb),0.45)' }}></span>
          </div>
        </div>
        <MicroCap sync>2 LINES SELECTED → STRINGOUT, ALREADY CUT TO LENGTH</MicroCap>
      </div>
    );
  }
  return null;
}

export function PremiereCapabilities() {
  const settled = useSettled();
  // `cd` staggers each card's demo loop (via the inherited --cd custom prop) so
  // the grid never pops in sync. Only the two heroes (smart drop, transcript)
  // carry the gradient ring + a caption; the four small cards get one-line copy.
  const caps: { kind: MicroKind; title: string; text: string; span: 2 | 4 | 6; hero?: boolean; cd: string }[] = [
    { kind: 'smart', title: 'Smart drop — never overwrites', text: 'The segment lands at your playhead, on the first track with room. Nothing gets stomped, nothing shifts — your cut stays your cut.', span: 4, hero: true, cd: '0s' },
    { kind: 'batch', title: 'Batch drop', text: 'Ten results, one place — back to back.', span: 2, cd: '0.7s' },
    { kind: 'match', title: 'Search by monitor frame', text: 'The frame you’re parked on becomes the search.', span: 2, cd: '1.4s' },
    { kind: 'source', title: 'Open in Source — in/out set', text: 'Points already marked. Cut it in your way.', span: 2, cd: '2.1s' },
    { kind: 'videoonly', title: 'Video-only drop', text: 'Just the picture — your mix stays sealed.', span: 2, cd: '2.8s' },
    { kind: 'transcript', title: 'Transcript-based edit', text: 'Select transcript lines, send a stringout straight to a new sequence — a paper edit that cuts itself.', span: 6, hero: true, cd: '1s' },
  ];
  const spanClass = (span: 2 | 4 | 6) => (span === 4 ? 'crw-cap-s4' : span === 6 ? 'crw-cap-s6' : 'crw-cap-s2');
  return (
    <section data-settled={settled ? 'true' : undefined} style={{ padding: '40px 24px 90px' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <SectionHead center label="Premiere panel — deeper" title="Watch it land." sub="Six moves the panel makes on your timeline — beyond finding the shot." />
        <div className="crw-capgrid">
          {caps.map((c) => {
            const cd = { ['--cd']: c.cd } as CSSProperties;
            const demo = <div className="crw-capcard-demo"><MicroClips kind={c.kind} /></div>;
            const copy = <div className="crw-capcard-copy"><b>{c.title}</b><p>{c.text}</p></div>;
            if (c.hero) {
              return (
                <div key={c.kind} className={`crw-capcard-hero ${spanClass(c.span)}`} style={cd}>
                  <div className="crw-capcard-in">{demo}{copy}</div>
                </div>
              );
            }
            return (
              <div key={c.kind} className={`crw-capcard ${spanClass(c.span)}`} style={cd}>{demo}{copy}</div>
            );
          })}
        </div>

        <div className="crw-libhead">
          <span className="crw-libhead-t">Your full local library, in Premiere</span>
          <span className="crw-libhead-rule"></span>
        </div>
        <div className="crw-libgrid">
          {/* 1 — project view: a mini bin tree, analyzed dots */}
          <div className="crw-libcard">
            <div className="crw-libviz">
              <div className="crw-librow"><span className="crw-glyph">▸</span>APOLLO_DOCU.PRPROJ</div>
              <div className="crw-librow" style={{ paddingLeft: 12 }}><span className="crw-glyph">├</span>NASA reels<span className="crw-num">34</span><span className="crw-ok"></span></div>
              <div className="crw-librow" style={{ paddingLeft: 12 }}><span className="crw-glyph">└</span>Newsreels<span className="crw-num">128</span><span className="crw-ok"></span></div>
            </div>
            <div><b>Project view</b><p>Browse the media of your current project — analyzed and searchable.</p></div>
          </div>
          {/* 2 — full local library: every indexed drive */}
          <div className="crw-libcard">
            <div className="crw-libviz">
              <div className="crw-librow"><span className="crw-glyph">▤</span>{'D:\\ FOOTAGE'}<span className="crw-num">2,410 clips</span><span className="crw-ok"></span></div>
              <div className="crw-librow"><span className="crw-glyph">▤</span>{'E:\\ ARCHIVE'}<span className="crw-num">5,872 clips</span><span className="crw-ok"></span></div>
              <div className="crw-librow"><span className="crw-glyph">▤</span>{'NAS\\ PROJECTS'}<span className="crw-num">1,204 clips</span><span className="crw-ok"></span></div>
            </div>
            <div><b>Full local library</b><p>Every indexed drive and folder, browsable without leaving Premiere.</p></div>
          </div>
          {/* 3 — media manage & search: relink / move across the library */}
          <div className="crw-libcard">
            <div className="crw-libviz">
              <div className="crw-libsearch"><span>⌕</span>the rocket on the pad — all drives</div>
              <div className="crw-librow" style={{ marginTop: 2 }}>apollo11-launch-1969.mp4<span className="crw-libchip" style={{ marginLeft: 'auto' }}>{'RELINKED → E:\\'}</span></div>
              <div className="crw-librow crw-dim">jfk-inauguration-1961.mp4<span className="crw-libchip" style={{ marginLeft: 'auto', color: 'var(--fg-3)', background: 'rgba(255,255,255,0.04)', borderColor: 'var(--line-1)' }}>MOVED</span></div>
            </div>
            <div><b>Media manage &amp; search</b><p>Find it, relink it, move it — across your whole library, from the panel.</p></div>
          </div>
        </div>
      </div>
    </section>
  );
}
